import { defineStore } from 'pinia'
import { ref, onMounted, onUnmounted } from 'vue'
import authService from '@/services/auth.service'
import type { LoginCredentials, NewPasswordCredentials, PhoneLoginCredentials, PhoneOTPVerificationCredentials, SocialLoginCredentials, ForgotPasswordData, ResetPasswordData } from '@/services/auth.service'
import type { User } from '@/services/user.service'
import type { PasswordActionData } from '@/enums/passwordActionType'

const ACTIVITY_EVENTS = [
  'click',
  'keydown',
  'mousedown',
  'touchstart'
]

const DEBOUNCE_DELAY = 120000 // 2 minutes debounce

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const isAuthenticated = ref(false)
  const phoneLoginSession = ref<string | null>(null)
  const lastAuthCheck = ref<number>(0)
  const authCheckPromise = ref<Promise<boolean> | null>(null)
  const tokenRefreshTimeout = ref<number | null>(null)
  const isRefreshing = ref(false)
  const isInitialized = ref(false)
  const isInitializing = ref(false)
  const lastRefreshTime = ref<number>(0)

  // Handle activity-triggered token refresh with debounce
  const handleActivity = async () => {
    const now = Date.now()
    if (now - lastRefreshTime.value < DEBOUNCE_DELAY) {
     
      return
    }

    if (!isRefreshing.value) {
      try {
        isRefreshing.value = true
        
        const response = await authService.refreshToken()
        if (response.success && response.accessToken) {
         
          token.value = response.accessToken
          localStorage.setItem('token', response.accessToken)
          lastRefreshTime.value = now
          setupTokenRefresh(response.accessToken)
        } else {
          console.error('Token refresh failed: Invalid response', response)
          await handleRefreshFailure()
        }
      } catch (error) {
        console.error('Token refresh failed:', error)
        await handleRefreshFailure()
      } finally {
        isRefreshing.value = false
      }
    }
  }

  // Setup token refresh with both expiration and activity-based refresh
  const setupTokenRefresh = (accessToken: string) => {
    // Clear any existing refresh timeout
    if (tokenRefreshTimeout.value) {
      window.clearTimeout(tokenRefreshTimeout.value)
    }

    try {
      // Parse JWT to get expiration
      const payload = JSON.parse(atob(accessToken.split('.')[1]))
      const expiresIn = payload.exp * 1000 - Date.now() - 1 * 60 * 1000 // 1 minute before expiry
      
     

      if (expiresIn > 0) {
        // Setup expiration-based refresh
        tokenRefreshTimeout.value = window.setTimeout(async () => {
          try {
            if (!isRefreshing.value) {
              isRefreshing.value = true
              const response = await authService.refreshToken()
              if (response.success && response.accessToken) {
                token.value = response.accessToken
                localStorage.setItem('token', response.accessToken)
                setupTokenRefresh(response.accessToken)
              } else {
                await handleRefreshFailure()
              }
            }
          } catch (error) {
            console.error('Token refresh failed:', error)
            await handleRefreshFailure()
          } finally {
            isRefreshing.value = false
          }
        }, expiresIn)

        // Add activity listeners for activity-based refresh
        ACTIVITY_EVENTS.forEach(event => {
          window.addEventListener(event, handleActivity)
        })
      } else {
        // Token is already expired
        handleRefreshFailure()
      }
    } catch (error) {
      console.error('Failed to setup token refresh:', error)
      handleRefreshFailure()
    }
  }

  // Remove activity listeners and clear timeout
  const cleanupTokenRefresh = () => {
    if (tokenRefreshTimeout.value) {
      window.clearTimeout(tokenRefreshTimeout.value)
      tokenRefreshTimeout.value = null
    }

    ACTIVITY_EVENTS.forEach(event => {
      window.removeEventListener(event, handleActivity)
    })
  }

  // Helper function to handle refresh failures
  const handleRefreshFailure = async () => {
    // Only redirect if not on login page and not initializing
    if (!window.location.pathname.includes('/login') && !isInitializing.value) {
      // Clear state
      user.value = null
      token.value = null
      isAuthenticated.value = false
      
      // Clear storage
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      
      // Clear any existing timeout
      if (tokenRefreshTimeout.value) {
        window.clearTimeout(tokenRefreshTimeout.value)
        tokenRefreshTimeout.value = null
      }

      window.location.href = '/login'
    }
  }

  // First, try to restore state from localStorage
  const restoreState = () => {
    const storedToken = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')
    
    if (storedToken && storedUser) {
      try {
        token.value = storedToken
        user.value = JSON.parse(storedUser)
        isAuthenticated.value = true
        return true
      } catch (error) {
        console.error('Failed to restore state:', error)
      }
    }
    return false
  }

  // Restore state immediately when store is created
  restoreState()

  const checkAuth = async () => {
    if (authCheckPromise.value) {
      return authCheckPromise.value
    }

    authCheckPromise.value = (async () => {
      try {
        if (!token.value) {
          return false
        }

        try {
          const response = await authService.getMe()
          if (response.success && response.data) {
            user.value = response.data
            localStorage.setItem('user', JSON.stringify(response.data))
            isAuthenticated.value = true
            setupTokenRefresh(token.value)
            return true
          }
        } catch (error) {
          console.error('Token verification failed:', error)
          // Try to refresh the token
          try {
            const refreshResponse = await authService.refreshToken()
            if (refreshResponse.success && refreshResponse.accessToken) {
              token.value = refreshResponse.accessToken
              localStorage.setItem('token', refreshResponse.accessToken)
              isAuthenticated.value = true
              setupTokenRefresh(refreshResponse.accessToken)
              return true
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError)
            await handleRefreshFailure()
          }
        }
        return false
      } finally {
        authCheckPromise.value = null
      }
    })()

    return authCheckPromise.value
  }

  // Initialize auth state
  const initialize = async () => {
    if (isInitialized.value || isInitializing.value) return
    
    isInitializing.value = true
    try {
      // Get stored data
      const storedToken = localStorage.getItem('token')
      const storedUser = localStorage.getItem('user')
      
      if (storedToken && storedUser) {
        try {
          // First try to refresh the token
          const refreshResponse = await authService.refreshToken()
          if (refreshResponse.success && refreshResponse.accessToken) {
            // Update token and user state
            token.value = refreshResponse.accessToken
            user.value = JSON.parse(storedUser)
            isAuthenticated.value = true
            
            // Update localStorage
            localStorage.setItem('token', refreshResponse.accessToken)
            
            // Setup token refresh
            setupTokenRefresh(refreshResponse.accessToken)
          } else {
            // If refresh failed, try to verify existing token
            token.value = storedToken
            user.value = JSON.parse(storedUser)
            isAuthenticated.value = true
            
            await checkAuth()
          }
        } catch (error) {
          console.error('Auth initialization failed:', error)
          await handleRefreshFailure()
        }
      }
    } finally {
      isInitialized.value = true
      isInitializing.value = false
    }
  }

  // Call initialize immediately
  initialize()

  const logout = () => {
    // Clear auth state
    user.value = null
    token.value = null
    isAuthenticated.value = false
    isInitialized.value = true
    
    // Clear localStorage
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    
    // Cleanup token refresh
    cleanupTokenRefresh()

    // Clear auth check promise
    authCheckPromise.value = null

    // Redirect to login page
    window.location.href = '/login'
  }

  const logoutOnly = () => {
    // Clear auth state
    user.value = null
    token.value = null
    isAuthenticated.value = false
    isInitialized.value = true
    
    // Clear localStorage
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    
    // Cleanup token refresh
    cleanupTokenRefresh()

    // Clear auth check promise
    authCheckPromise.value = null
  }

  const login = async (credentials: LoginCredentials) => {
    try {
      // Don't clear state before attempting login
      const response = await authService.login(credentials)
     

      // Handle NEW_PASSWORD_REQUIRED challenge
      if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
        return response
      }

      if (!response.success || !response.accessToken || !response.user) {
        throw new Error('Invalid response format from server')
      }

      // Store token and user data
      token.value = response.accessToken
      user.value = response.user
      isAuthenticated.value = true
      
      // Persist to localStorage
      localStorage.setItem('token', response.accessToken)
      localStorage.setItem('user', JSON.stringify(response.user))

      // Setup token refresh
      setupTokenRefresh(response.accessToken)

      // Initialize auth state after successful login
      await initialize()

      return response
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  const skipNewPassword = async (email: string, password: string) => {
    try {
      const response = await authService.skipNewPassword({ email, password })
     

      if (!response.success || !response.accessToken || !response.user) {
        throw new Error('Invalid response format from server')
      }

      // Store token and user data
      token.value = response.accessToken
      user.value = response.user
      isAuthenticated.value = true
      localStorage.setItem('token', response.accessToken)
      localStorage.setItem('user', JSON.stringify(response.user))

      // Setup token refresh
      setupTokenRefresh(response.accessToken)

      return response
    } catch (error) {
      console.error('Skip password failed:', error)
      throw error
    }
  }

  const completeNewPassword = async (credentials: NewPasswordCredentials) => {
   
    try {
     
      const response = await authService.completeNewPassword(credentials)
     

      // Store user data and token
      if (!response.user || !response.accessToken) {
        throw new Error('Invalid response format: missing user or token')
      }

      user.value = response.user
      token.value = response.accessToken
      isAuthenticated.value = true

      // Always save token to localStorage
      localStorage.setItem('token', response.accessToken)
     

      return response
    } catch (error) {
      console.error('Auth store: Password change failed:', error)
      await logout()
      throw error
    }
  }

  const initiatePhoneLogin = async (credentials: PhoneLoginCredentials) => {
   
    try {
     
      const response = await authService.initiatePhoneLogin(credentials)
     

      if (!response.success) {
        throw new Error('Phone login initiation failed')
      }

      // Store the session for OTP verification
      phoneLoginSession.value = response.session
      return response
    } catch (error) {
      console.error('Auth store: Phone login initiation failed:', error)
      throw error
    }
  }

  const verifyPhoneOTP = async (credentials: PhoneOTPVerificationCredentials) => {
  
    try {
     
      const response = await authService.verifyPhoneOTP(credentials)
     

      if (!response.success || !response.accessToken || !response.user) {
        throw new Error('Invalid response format from server')
      }

      user.value = response.user
      token.value = response.accessToken
      isAuthenticated.value = true
      phoneLoginSession.value = null

      // Always save token to localStorage
      localStorage.setItem('token', response.accessToken)
     

      // Save user data in localStorage for phone login
      localStorage.setItem('user', JSON.stringify(response.user))
     

      return response
    } catch (error) {
      console.error('Auth store: OTP verification failed:', error)
      throw error
    }
  }

  const resendPhoneOTP = async (credentials: PhoneLoginCredentials) => {
   
    try {
     
      const response = await authService.resendPhoneOTP(credentials)
     

      if (!response.success) {
        throw new Error('OTP resend failed')
      }

      // Update the session if provided
      if (response.session) {
        phoneLoginSession.value = response.session
      }

      return response
    } catch (error) {
      console.error('Auth store: OTP resend failed:', error)
      throw error
    }
  }

  const loginWithSocial = async (credentials: SocialLoginCredentials) => {
   
    try {
      const response = await authService.loginWithSocial(credentials)
     

      if (!response.success || !response.accessToken || !response.user) {
        throw new Error('Invalid response format from server')
      }

      // Store token and user data
      token.value = response.accessToken
      user.value = response.user
      isAuthenticated.value = true
      
      // Persist to localStorage
      localStorage.setItem('token', response.accessToken)
      localStorage.setItem('user', JSON.stringify(response.user))

      // Setup token refresh
      setupTokenRefresh(response.accessToken)

      return response
    } catch (error) {
      console.error('Auth store: Social login failed:', error)
      throw error
    }
  }

  const forgotPassword = async (email: string) => {
    try {
      const response = await authService.forgotPassword({ email })
      return response
    } catch (error) {
      console.error('Forgot password error:', error)
      throw error
    }
  }

  const resetPassword = async (credentials: ResetPasswordData) => {
    try {
      const response = await authService.resetPassword(credentials)
      return response
    } catch (error) {
      console.error('Reset password error:', error)
      throw error
    }
  }

  const logPasswordAction = async (credentials: PasswordActionData) => {
    try {
      const response = await authService.logPasswordAction(credentials)
      return response
    } catch (error) {
      console.error('Log password action error:', error)
      throw error
    }
  }

  // Initialize user from localStorage if available
  const storedUser = localStorage.getItem('user')
  if (storedUser) {
    try {
      user.value = JSON.parse(storedUser)
    } catch (error) {
      console.error('Failed to parse stored user:', error)
      localStorage.removeItem('user')
    }
  }

  // Cleanup on store destruction
  onUnmounted(() => {
    cleanupTokenRefresh()
  })

  return {
    user,
    token,
    isAuthenticated,
    isInitialized,
    isInitializing,
    phoneLoginSession,
    checkAuth,
    initialize,
    login,
    logout,
    logoutOnly,
    skipNewPassword,
    completeNewPassword,
    initiatePhoneLogin,
    verifyPhoneOTP,
    resendPhoneOTP,
    handleRefreshFailure,
    refreshToken: async () => {
      try {
        const response = await authService.refreshToken()
        if (response.success && response.accessToken) {
          token.value = response.accessToken
          localStorage.setItem('token', response.accessToken)
          setupTokenRefresh(response.accessToken)
          return response
        }
        throw new Error('Token refresh failed')
      } catch (error) {
        console.error('Token refresh failed:', error)
        throw error
      }
    },
    setToken: (newToken: string) => {
      token.value = newToken
      localStorage.setItem('token', newToken)
      setupTokenRefresh(newToken)
    },
    setUser: (newUser: User) => {
      user.value = newUser
      localStorage.setItem('user', JSON.stringify(newUser))
      isAuthenticated.value = true
    },
    setAuthenticated: (value: boolean) => {
      isAuthenticated.value = value
    },
    setImpersonationAuth: (impersonationToken: string, impersonatedUser: any) => {
      token.value = impersonationToken
      user.value = {
        _id: impersonatedUser.id,
        email: impersonatedUser.email,
        firstName: impersonatedUser.name.split(' ')[0] || '',
        lastName: impersonatedUser.name.split(' ').slice(1).join(' ') || '',
        role: 'impersonated',
        companyId: '',
        isActive: true,
        isImpersonated: true,
        impersonatedBy: impersonatedUser.impersonated_by
      } as User
      isAuthenticated.value = true
      localStorage.setItem('token', impersonationToken)
      localStorage.setItem('user', JSON.stringify(user.value))
    },
    loginWithJWT: async (jwtToken: string) => {
      try {  
        // First, trigger a proper logout to clear backend session
        if (isAuthenticated.value) {

          try {
            await authService.logout()

          } catch (logoutError) {
            console.warn('JWT Login: Backend logout failed, continuing anyway:', logoutError)
          }
        }
        
        // Clear existing auth state and localStorage first (like logout does)
        user.value = null
        token.value = null
        isAuthenticated.value = false
        
        // Clear ALL possible authentication-related localStorage items
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        localStorage.removeItem('impersonation_token')
        localStorage.removeItem('impersonated_user')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('sessionToken')
        
        // Clear any other potential auth items
        Object.keys(localStorage).forEach(key => {
          if (key.toLowerCase().includes('auth') || 
              key.toLowerCase().includes('token') || 
              key.toLowerCase().includes('user') ||
              key.toLowerCase().includes('session')) {

            localStorage.removeItem(key)
          }
        })
        

        
        cleanupTokenRefresh()
        
        // Also clear sessionStorage for good measure
        sessionStorage.clear()
        
        // Small delay to ensure state is cleared
        await new Promise(resolve => setTimeout(resolve, 100))
        
        const response = await authService.loginWithJWT({ token: jwtToken })
        
        if (response.success) {

          token.value = response.accessToken
          user.value = response.user
          isAuthenticated.value = true
          localStorage.setItem('token', response.accessToken)
          localStorage.setItem('user', JSON.stringify(response.user))
          setupTokenRefresh(response.accessToken)
          

        }
        
        return response
      } catch (error) {
        console.error('JWT login failed:', error)
        throw error
      }
    },
    loginWithSocial,
    forgotPassword,
    resetPassword,
    logPasswordAction
  }
}) 